import { useFormatter, useTranslations } from "next-intl";
import { buttonClasses } from "@/components/ui/button";
import { apiErrorText } from "@/lib/api-error";
import {
  dismissVideoJob,
  lessonVideoBusy,
  retryVideoStatus,
  startLessonVideoUpload,
  useVideoJobs,
  type VideoJob,
} from "@/services/api/video-upload-store";

type LessonVideoUploadProps = {
  courseId: string;
  lessonId: string;
  hasVideo: boolean;
  /** Another lesson's transfer is running: one upload at a time keeps the line for it. */
  otherUploadRunning: boolean;
  describedBy?: string;
  /** FR-14 a: no Bunny Stream library for this school; the message replaces the control. */
  notConfigured?: boolean;
};

/**
 * Upload / Replace for one lesson, with the state of its hosted video (CRS-2, CRS-8): progress,
 * "processing since …" (also after a reload), an interrupted upload to resume, a stalled status
 * check, or a translated failure.
 */
export function LessonVideoUpload({
  courseId,
  lessonId,
  hasVideo,
  otherUploadRunning,
  describedBy,
  notConfigured = false,
}: LessonVideoUploadProps) {
  const t = useTranslations("instructor");
  const job = useVideoJobs()[lessonId];
  const busy = lessonVideoBusy(job);
  const disabled = busy || (otherUploadRunning && job?.phase !== "uploading");

  const label =
    job?.phase === "uploading"
      ? job.waitingForNetwork
        ? t("uploadWaitingForNetwork")
        : job.percent
          ? t("uploadingVideoPercent", { percent: job.percent })
          : t("uploadingVideo")
      : hasVideo
        ? t("replaceVideo")
        : t("uploadVideo");

  if (notConfigured) {
    return (
      <span
        className="ml-2 text-sm text-[var(--text-muted)]"
        data-testid={`video-storage-not-configured-${lessonId}`}
      >
        {t("videoStorageNotConfigured")}
      </span>
    );
  }

  return (
    <>
      <label
        className={`relative z-10 ml-2 ${disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"} focus-within:ring-2 focus-within:ring-[var(--accent-ring)] ${buttonClasses({ variant: "secondary", size: "sm" })}`}
        data-testid={`upload-video-${lessonId}`}
        aria-disabled={disabled}
      >
        {label}
        <input
          type="file"
          accept="video/*"
          className="sr-only"
          disabled={disabled}
          aria-describedby={describedBy}
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = "";
            if (file) void startLessonVideoUpload(courseId, lessonId, file);
          }}
        />
      </label>
      {job ? <LessonVideoJobStatus lessonId={lessonId} job={job} /> : null}
    </>
  );
}

function LessonVideoJobStatus({ lessonId, job }: { lessonId: string; job: VideoJob }) {
  const t = useTranslations("instructor");
  const tRoot = useTranslations();
  const format = useFormatter();
  const when = (iso: string) => {
    const date = new Date(iso);
    return Number.isNaN(date.getTime())
      ? iso
      : format.dateTime(date, {
          dateStyle: "medium",
          timeStyle: "short",
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        });
  };

  if (job.phase === "uploading") return null;

  if (job.phase === "processing") {
    return (
      <span
        className="ml-2 text-[var(--text-muted)]"
        data-testid={`video-processing-${lessonId}`}
        role="status"
      >
        · {t("videoProcessingSince", { time: when(job.since) })}
      </span>
    );
  }

  if (job.phase === "interrupted") {
    return (
      <span className="ml-2 text-[var(--accent-ink)]" data-testid={`video-interrupted-${lessonId}`}>
        ·{" "}
        {job.fileName
          ? t("videoUploadInterrupted", { file: job.fileName, time: when(job.since) })
          : t("videoUploadInterruptedNoName", { time: when(job.since) })}
      </span>
    );
  }

  const message =
    job.phase === "stalled"
      ? t("videoStatusStalled")
      : apiErrorText(job.error, tRoot, "instructor.uploadVideoFailed", {
          namespace: "instructor.errors",
        });

  return (
    <span
      className="ml-2 text-[var(--danger)]"
      role="alert"
      data-testid={`video-error-${lessonId}`}
    >
      · {message}{" "}
      {job.phase === "stalled" ? (
        <button
          type="button"
          className="underline"
          data-testid={`video-check-again-${lessonId}`}
          onClick={() => retryVideoStatus(lessonId)}
        >
          {t("videoStatusCheckAgain")}
        </button>
      ) : (
        <button
          type="button"
          className="underline"
          data-testid={`video-dismiss-${lessonId}`}
          onClick={() => dismissVideoJob(lessonId)}
        >
          {t("dismiss")}
        </button>
      )}
    </span>
  );
}

/**
 * CRS-8: transfers keep running while the instructor moves between builder steps (or pages);
 * this says so wherever they are, with the percentage.
 */
export function VideoUploadIndicator() {
  const t = useTranslations("instructor");
  const uploads = Object.entries(useVideoJobs()).filter(
    (entry): entry is [string, Extract<VideoJob, { phase: "uploading" }>] =>
      entry[1].phase === "uploading",
  );
  if (uploads.length === 0) return null;
  return (
    <div
      role="status"
      aria-label={t("videoUploadIndicatorLabel")}
      data-testid="video-upload-indicator"
      className="rounded-md border border-[var(--border)] bg-[var(--surface-2)] px-3 py-2 text-sm"
    >
      {uploads.map(([lessonId, job]) => (
        <p key={lessonId}>
          {job.waitingForNetwork
            ? `${job.fileName}: ${t("uploadWaitingForNetwork")}`
            : t("videoUploadIndicator", { file: job.fileName, percent: job.percent })}
        </p>
      ))}
    </div>
  );
}
